import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import AppIcon from '../../components/common/AppIcon/AppIcon';
import ButtonSpinner from '../../components/common/ButtonSpinner/ButtonSpinner';
import { useToast } from '../../context/ToastContext';
import { getMyProjects } from '../../services/projectService';
import { getUpcomingClasses } from '../../services/classService';
import {
  getResubmissionRequests, decideResubmission,
} from '../../services/competitionWorkflowService';
import { formatDate, formatTime } from '../../utils/helpers';
import styles from './MentorDashboard.module.css';

const MentorDashboard = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [resubRequests, setResubRequests] = useState([]);
  const [deciding, setDeciding] = useState(null);   // `${id}:approve` | `${id}:deny`

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Requests are their own call and their own failure: a competition the
      // mentor has nothing to do with must not blank the whole dashboard.
      const [projectsData, classesData] = await Promise.all([
        getMyProjects(),
        getUpcomingClasses()
      ]);
      setProjects(projectsData);
      setUpcomingClasses(classesData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
    loadResubRequests();
  };

  const loadResubRequests = async () => {
    try {
      setResubRequests(await getResubmissionRequests());
    } catch (error) {
      console.error('Error fetching resubmission requests:', error);
    }
  };

  const decide = async (request, approve) => {
    setDeciding(`${request.id}:${approve ? 'approve' : 'deny'}`);
    try {
      await decideResubmission(request.id, approve);
      toast.success(approve
        ? `${request.component_title} reopened for ${request.student_name}.`
        : 'Request denied. The student has been told.');
      loadResubRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not decide the request.');
    } finally {
      setDeciding(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading dashboard..." />
      </div>
    );
  }

  const activeProjects = projects.filter(p => p.status === 'in_progress').length;
  const pendingScopes = projects.filter(p => !p.project_scope).length;

  return (
    <div className={styles.dashboard}>
      <h1 className={styles.pageTitle}>Mentor Dashboard</h1>

      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="projects" size={34} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{activeProjects}</h3>
            <p className={styles.statLabel}>Active Projects</p>
            <span className={styles.statSubtext}>out of {projects.length} total</span>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="classes" size={34} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{upcomingClasses.length}</h3>
            <p className={styles.statLabel}>Upcoming Classes</p>
            <span className={styles.statSubtext}>next 5 scheduled</span>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="assignments" size={34} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{pendingScopes}</h3>
            <p className={styles.statLabel}>Pending Actions</p>
            <span className={styles.statSubtext}>scopes to submit</span>
          </div>
        </Card>
      </div>

      {/* Students waiting on a decision. Full width and above the standing
          lists, because every row here is someone blocked on this mentor —
          the WhatsApp tag they already got points at this. Hidden entirely
          when there is nothing to decide. */}
      {resubRequests.length > 0 && (
        <Card title={`Resubmission Requests (${resubRequests.length})`} className={styles.resubCard}>
          <div className={styles.resubList}>
            {resubRequests.map((r) => (
              <div key={r.id} className={styles.resubItem}>
                <div className={styles.resubInfo}>
                  <h4>{r.student_name}</h4>
                  <p className={styles.resubWhat}>
                    {r.component_title}
                    {r.competition_name ? ` · ${r.competition_name}` : ''}
                  </p>
                  <p className={styles.resubWhy}>“{r.reason}”</p>
                  <span className={styles.resubWhen}>asked {formatDate(r.created_at)}</span>
                </div>
                <div className={styles.resubButtons}>
                  <button
                    className={styles.resubDeny}
                    disabled={deciding !== null}
                    onClick={() => decide(r, false)}
                  >
                    {deciding === `${r.id}:deny` && <ButtonSpinner size={11} />}Deny
                  </button>
                  <button
                    className={styles.resubAllow}
                    disabled={deciding !== null}
                    onClick={() => decide(r, true)}
                  >
                    {deciding === `${r.id}:approve` && <ButtonSpinner size={11} />}Allow
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className={styles.sectionsGrid}>
        <Card title="My Projects">
          <div className={styles.projectsList}>
            {projects.length === 0 ? (
              <p className={styles.empty}>No projects assigned</p>
            ) : (
              projects.map(project => (
                <div key={project.id} className={styles.projectItem}>
                  <div className={styles.projectInfo}>
                    <h4>{project.project_name}</h4>
                    <p className={styles.projectTrack}>{project.project_track.replace(/_/g, ' ').toUpperCase()}</p>
                  </div>
                  <StatusBadge status={project.status} />
                </div>
              ))
            )}
          </div>
        </Card>

        <Card title="Upcoming Classes">
          <div className={styles.classesList}>
            {upcomingClasses.length === 0 ? (
              <p className={styles.empty}>No upcoming classes</p>
            ) : (
              upcomingClasses.map(cls => (
                <div key={cls.id} className={styles.classItem}>
                  <div className={styles.classInfo}>
                    <h4>Class {cls.class_number}</h4>
                    <p className={styles.classDate}>{formatDate(cls.class_date)} at {formatTime(cls.class_time)}</p>
                  </div>
                  {cls.meet_link && (
                    <a href={cls.meet_link} target="_blank" rel="noopener noreferrer" className={styles.joinBtn}>
                      Join
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MentorDashboard;
